import {useState} from 'react';
import {notification} from 'antd';
import {BoardService} from './boardService';

export const useStatusManagement = ({boardId, statuses, onStatusesChange}) => {
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editingStatus, setEditingStatus] = useState(null);

    const openStatusModal = () => setIsStatusModalOpen(true);
    const closeStatusModal = () => {
        setIsStatusModalOpen(false);
        setEditingStatus(null);
    };

    const addStatus = async (values) => {
        try {
            setLoading(true);
            const colorHex = values.color;
            const newStatus = await BoardService.createStatus(boardId, {
                name: values.name,
                color: (typeof colorHex === 'string' ? colorHex : colorHex?.toHexString())
            });

            onStatusesChange([...statuses, newStatus]);

            notification.success({
                message: 'Status Added',
                description: `Status "${values.name}" has been added successfully.`
            });

            return newStatus;
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to add status. Please try again.'
            });
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (statusId, values) => {
        try {
            setLoading(true);
            const updatedStatus = await BoardService.updateStatus(boardId, statusId, values);

            onStatusesChange(statuses.map(status =>
                status.id === statusId ? updatedStatus : status
            ));

            notification.success({
                message: 'Status Updated',
                description: 'Status has been updated successfully.'
            });

            return updatedStatus;
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to update status. Please try again.'
            });
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const deleteStatus = async (statusId) => {
        try {
            setLoading(true);
            await BoardService.deleteStatus(boardId, statusId);

            onStatusesChange(statuses.filter(s => s.id !== statusId));

            notification.success({
                message: 'Status Deleted',
                description: 'Status has been deleted successfully.'
            });
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to delete status. Please try again.'
            });
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const startEditing = (status) => {
        setEditingStatus({
            id: status.id,
            name: status.name,
            color: status.color
        });
    };

    const cancelEditing = () => {
        setEditingStatus(null);
    };

    return {
        isStatusModalOpen,
        openStatusModal,
        closeStatusModal,
        loading,
        editingStatus,
        addStatus,
        updateStatus,
        deleteStatus,
        startEditing,
        cancelEditing
    };
};
